import { useState } from 'react';
import { Modal } from '../../../components/Modal';
import { AddBookForm } from '../components/AddBookForm';
import { toast } from 'react-toastify';
import { BookCreate } from '../api/types';
import { useAddBookMutation } from '../api/booksApi';
import { useNavigate } from 'react-router-dom';

type CreateBookProps = {
  isOpen: boolean;
  onCloseModal: () => void;
};

export const useCreateBook = ({ isOpen, onCloseModal }: CreateBookProps) => {
  const navigate = useNavigate();
  const [addBook, { isLoading }] = useAddBookMutation();

  const [isAiProcessing, setIsAiProcessing] = useState(true);
  const [values, setValues] = useState<BookCreate>({
    cover: null,
    back: null,
    title: '',
    author_name: '',
    price: '',
    category_name: '',
    publisher_name: '',
    language: '',
    year: 0,
    pages: 0,
    synopsis: ''
  });

  const onClose = () => {
    setValues({
      cover: null,
      back: null,
      title: '',
      author_name: '',
      price: '',
      category_name: '',
      publisher_name: ''
    });
    onCloseModal();
  };

  const handleImageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    type: 'back' | 'cover'
  ) => {
    const image = event.target.files?.[0];
    if (!image) {
      toast('Error uploading image', { type: 'error' });
      return;
    }
    setValues({ ...values, [type]: image });
  };

  const onValueChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setValues({ ...values, [name]: value });
  };

  const addWithAi = async () => {
    const formData = new FormData();
    if (!values.cover) {
      toast('Please upload a cover image', { type: 'error' });
      return;
    }
    formData.append('is_ai_generated', 'true');
    formData.append('cover', values.cover);
    if (values.back) {
      formData.append('back', values.back);
    }
    try {
      addBook({ formData }).unwrap();
      onClose();
      navigate('/my-books/draft');
    } catch (err) {
      console.error(err);
    }
  };

  const addManual = async () => {
    try {
      const formData = new FormData();
      if (!values.title || !values.author_name || !values.price || !values.category_name) {
        toast('Please fill in all required fields', { type: 'error' });
        return;
      }

      if (!values.cover) {
        toast('Please upload a cover image', { type: 'error' });
        return;
      }
      formData.append('cover', values.cover);
      if (values.back) {
        formData.append('back', values.back);
      }

      formData.append('title', values.title);
      formData.append('author_name', values.author_name);
      formData.append('price', values.price);
      formData.append('category_name', values.category_name);
      if (values.publisher_name) {
        formData.append('publisher_name', values.publisher_name);
      }
      if (values.language) {
        formData.append('language', values.language);
      }
      if (values.year) {
        formData.append('year', values.year.toString());
      }
      if (values.pages) {
        formData.append('pages', values.pages.toString());
      }
      if (values.synopsis) {
        formData.append('synopsis', values.synopsis);
      }

      addBook({ formData }).unwrap();
      onClose();
    } catch (err) {
      console.error(err);
    }
  };

  const onAddBook = async () => {
    if (isAiProcessing) {
      await addWithAi();
    } else {
      await addManual();
    }
  };

  const CreateBookModal = (
    <Modal isOpen={isOpen} onClose={onClose}>
      <AddBookForm
        isAiProcessing={isAiProcessing}
        setIsAiProcessing={setIsAiProcessing}
        handleImageChange={handleImageChange}
        values={values}
        onChange={onValueChange}
        onAddBook={onAddBook}
        onClose={onClose}
        isLoading={isLoading}
      />
    </Modal>
  );
  return { CreateBookModal };
};
